import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { STAGE_LABELS, timeAgo, ROLE_LABELS } from "@/lib/constants";
import SLABadge from "@/components/SLABadge";
import TrackerTable from "@/components/TrackerTable";
import { UserCheck, Users2, Layers, AlertTriangle, CheckSquare, Square, Send } from "lucide-react";

export default function ManagerConsole() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [assignAnalyst, setAssignAnalyst] = useState("");
  const [assignAttorney, setAssignAttorney] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/workspace/manager-console"); setData(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load console"); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSelected(prev => {
    const s = new Set(prev);
    if (s.has(id)) s.delete(id); else s.add(id);
    return s;
  });
  const toggleAll = () => {
    if (!data) return;
    if (selected.size === data.unassigned.length) setSelected(new Set());
    else setSelected(new Set(data.unassigned.map(c => c.id)));
  };

  const bulk = async () => {
    if (selected.size === 0) return toast.error("Select at least one contract");
    if (!assignAnalyst && !assignAttorney) return toast.error("Pick an analyst or attorney");
    setBusy(true);
    try {
      const { data: r } = await api.post("/workspace/bulk-assign", {
        contract_ids: [...selected],
        analyst_id: assignAnalyst || null,
        attorney_id: assignAttorney || null,
      });
      toast.success(`Assigned ${r.assigned} / ${r.requested} contracts`);
      setSelected(new Set()); setAssignAnalyst(""); setAssignAttorney("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk assign failed"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="p-6 text-slate-500 text-sm">Loading…</div>;
  const analysts = data.team.filter(t => t.role === "contract_analyst");
  const attorneys = data.team.filter(t => t.role === "attorney");

  return (
    <div className="p-6" data-testid="manager-console-page">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Director of Legal Team</div>
        <h1 className="text-2xl font-bold text-slate-900">Triage &amp; Assignment</h1>
        <p className="text-sm text-slate-500 mt-1">
          Route new intakes to the right contract analyst or attorney. Bulk-assign, monitor team workload, and audit every move.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-3 mb-6" data-testid="mc-kpis">
        <KPI icon={Layers}  label="Unassigned" value={data.kpis.unassigned} tone={data.kpis.unassigned > 0 ? "warn" : "ok"}/>
        <KPI icon={Users2}  label="Team Size" value={data.kpis.team_size}/>
        <KPI icon={UserCheck} label="Active Load" value={data.kpis.team_active_load}/>
        <KPI icon={AlertTriangle} label="Overdue" value={data.kpis.team_overdue} tone={data.kpis.team_overdue > 0 ? "danger" : "ok"}/>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Unassigned queue */}
        <div className="col-span-3 bg-white border border-slate-200 rounded-md">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
            <Layers className="w-4 h-4 text-[#005289]"/>
            <div className="text-sm font-bold text-slate-900">Unassigned Queue — Legal Tracker</div>
            <span className="ml-auto text-xs text-slate-500">{data.unassigned.length}</span>
          </div>
          {/* Bulk assign toolbar */}
          {selected.size > 0 && (
            <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex flex-wrap gap-2 items-center" data-testid="bulk-toolbar">
              <span className="text-xs font-semibold text-slate-700">{selected.size} selected</span>
              <select data-testid="bulk-analyst" value={assignAnalyst} onChange={e => setAssignAnalyst(e.target.value)}
                      className="h-8 px-2 border border-slate-200 rounded text-xs">
                <option value="">— Contract Analyst —</option>
                {analysts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.workload.total})</option>)}
              </select>
              <select data-testid="bulk-attorney" value={assignAttorney} onChange={e => setAssignAttorney(e.target.value)}
                      className="h-8 px-2 border border-slate-200 rounded text-xs">
                <option value="">— Attorney —</option>
                {attorneys.map(a => <option key={a.id} value={a.id}>{a.name} ({a.workload.total})</option>)}
              </select>
              <button data-testid="bulk-assign-btn" onClick={bulk} disabled={busy}
                      className="h-8 px-3 bg-[#005289] text-white rounded text-xs font-semibold flex items-center gap-1 disabled:opacity-60">
                <Send className="w-3 h-3"/> Assign
              </button>
              <button onClick={() => setSelected(new Set())} className="h-8 px-3 border border-slate-200 rounded text-xs">Clear</button>
            </div>
          )}
          <div className="px-3 py-2 border-b border-slate-200 bg-slate-50/50 flex items-center gap-2 text-xs">
            <button onClick={toggleAll} data-testid="select-all" className="p-1 hover:bg-slate-100 rounded">
              {selected.size === data.unassigned.length && data.unassigned.length > 0 ? <CheckSquare className="w-4 h-4"/> : <Square className="w-4 h-4"/>}
            </button>
            <span className="text-slate-500">Select rows below via checkbox column, or use "Select all" ({selected.size} selected)</span>
          </div>
          <div className="border-t border-slate-100" data-testid="unassigned-list">
            <TrackerTable rows={data.unassigned} selection={selected} onToggle={toggle} onChanged={load} testIdPrefix="mc-tt" emptyText="Inbox zero"/>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6 mt-6">
        <div className="col-span-2"/>
        {/* Team workload */}
        <div className="bg-white border border-slate-200 rounded-md" data-testid="team-workload">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
            <Users2 className="w-4 h-4 text-[#005289]"/>
            <div className="text-sm font-bold text-slate-900">Team Workload</div>
          </div>
          <div className="p-2 space-y-1">
            {data.team.map(t => (
              <div key={t.id} className="px-3 py-2 hover:bg-slate-50 rounded" data-testid={`team-${t.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{t.name}</div>
                    <div className="text-[10px] text-slate-500">{ROLE_LABELS[t.role] || t.role} · {t.department || "—"}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-slate-900">{t.workload.total}</div>
                    {t.workload.overdue > 0 && <div className="text-[10px] text-red-600 font-semibold">{t.workload.overdue} overdue</div>}
                  </div>
                </div>
                <div className="flex gap-2 mt-1 text-[10px]">
                  <span className="text-slate-500">Not started {t.workload.not_started}</span>
                  <span className="text-slate-500">· In progress {t.workload.in_progress}</span>
                  <span className="text-slate-500">· External {t.workload.pending_external}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent assignments */}
      {data.recent_assignments.length > 0 && (
        <div className="mt-6 bg-white border border-slate-200 rounded-md" data-testid="recent-assignments">
          <div className="px-5 py-3 border-b border-slate-200 text-sm font-bold text-slate-900">Recent Assignments</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-xs text-slate-500 uppercase tracking-wider">
                <th className="text-left px-3 py-2">#</th>
                <th className="text-left px-3 py-2">Vendor</th>
                <th className="text-left px-3 py-2">Analyst</th>
                <th className="text-left px-3 py-2">Attorney</th>
                <th className="text-left px-3 py-2">Stage</th>
                <th className="text-left px-3 py-2">Updated</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_assignments.map(c => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/contract/${c.id}`)}>
                  <td className="px-3 py-2 font-mono text-xs">{c.contract_number}</td>
                  <td className="px-3 py-2 font-semibold">{c.vendor_legal_name}</td>
                  <td className="px-3 py-2 text-xs">{c.assigned_analyst_name || "—"}</td>
                  <td className="px-3 py-2 text-xs">{c.assigned_attorney_name || "—"}</td>
                  <td className="px-3 py-2"><span className="chip chip-draft"><span className="dot"/>{STAGE_LABELS[c.stage] || c.stage}</span></td>
                  <td className="px-3 py-2 text-xs text-slate-500">{timeAgo(c.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, tone = "neutral" }) {
  const color = tone === "danger" ? "text-red-600" : tone === "warn" ? "text-amber-600" : "text-slate-900";
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3">
      <div className="flex items-center gap-2 text-[10px] uppercase text-slate-500 font-bold tracking-wider"><Icon className="w-3 h-3"/> {label}</div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}
